import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CheckCircle2, Hourglass, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function BillingSuccess() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const orderId = sp.get("order_id");
  const [status, setStatus] = useState({ status: "checking" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!orderId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/orders/${orderId}/status`);
        if (stopped) return;
        setStatus(data);
        if (data.status === "active" || ["failed", "cancelled", "expired"].includes(data.status)) {
          clearInterval(timer);
        }
      } catch {
        if (stopped) return;
        setStatus({ status: "error" });
      } finally {
        setTries((t) => t + 1);
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => { stopped = true; clearInterval(timer); };
  }, [orderId]);

  const config = {
    active: { icon: CheckCircle2, label: "Payment successful", color: "text-emerald-500", desc: `Your workspace is now on the ${status.plan_slug || ""} plan.` },
    pending_payment: { icon: Hourglass, label: "Waiting for payment", color: "text-yellow-500", desc: "We're awaiting confirmation from Cashfree…" },
    pending_gateway: { icon: Hourglass, label: "Gateway not configured", color: "text-yellow-500", desc: "Platform admin must configure Cashfree first." },
    failed: { icon: XCircle, label: "Payment failed", color: "text-red-500", desc: "Please try again or use a different method." },
    cancelled: { icon: XCircle, label: "Cancelled", color: "text-muted-foreground", desc: "You cancelled the payment." },
    expired: { icon: XCircle, label: "Order expired", color: "text-muted-foreground", desc: "The payment window expired." },
    checking: { icon: Hourglass, label: "Checking status…", color: "text-muted-foreground", desc: `Polled ${tries} times` },
    error: { icon: XCircle, label: "Could not fetch status", color: "text-red-500", desc: "Try refreshing the page." },
  };
  const c = config[status.status] || config.checking;
  const Icon = c.icon;

  return (
    <>
      <PageHeader eyebrow="Checkout" title="Order status" testid="billing-success-header" />
      <div className="px-4 sm:px-6 md:px-10 py-12 max-w-2xl">
        <div className="border border-border p-10 bg-card" data-testid={`order-status-${status.status}`}>
          <Icon className={`w-12 h-12 mb-4 ${c.color}`} />
          <h2 className="heading-display text-3xl mb-3">{c.label}</h2>
          <p className="text-muted-foreground mb-2">{c.desc}</p>
          <p className="text-xs text-muted-foreground mb-6">Order: <span className="font-mono text-foreground">{orderId}</span></p>
          <div className="flex gap-3">
            <Button onClick={() => nav("/app/billing")} className="rounded-none" data-testid="success-back-btn">Back to billing</Button>
            <Link to="/app"><Button variant="outline" className="rounded-none" data-testid="success-dash-btn">Go to dashboard</Button></Link>
          </div>
        </div>
      </div>
    </>
  );
}
